import { Grid, MenuItem, TextField } from '@mui/material'
import { t } from 'i18next'
import Joi from 'joi'
import { useEffect, useState } from 'react'

import useFieldValidation from '../../../helpers/fieldValidation.js'
import { useValidationEffect } from '../../../helpers/hooks.js'

const buildSchema = (technology) => {
  const showLora = technology === 'Lorawan' || technology === 'Wmbus+Lorawan'
  const showWmbus = technology === 'Wmbus' || technology === 'Wmbus+Lorawan'

  return Joi.object({
    gatewayId: showLora
      ? Joi.string().pattern(/^[0-9a-fA-F]{16}$/).required()
      : Joi.string().allow(null, '').optional(),
    serial: Joi.string().required(),
    siteId: Joi.string().allow(null, '').optional(),
    lat: Joi.number().min(-85.05112878).max(85.05112878).optional(),
    lng: Joi.number().min(-180).max(180).optional(),
    status: Joi.string().valid('Attivo', 'Inattivo', 'Manutenzione').required(),
    technology: Joi.string().valid('Lorawan', 'Wmbus', 'Wmbus+Lorawan').required(),
    wmbusId: showWmbus ? Joi.string().required() : Joi.string().allow(null, '').optional(),
    antennaLora: showLora ? Joi.string().required() : Joi.string().allow(null, '').optional(),
    antennaWmbus: showWmbus ? Joi.string().required() : Joi.string().allow(null, '').optional(),
    connectivity: Joi.string().valid('SIM', 'Ethernet', 'WiFi').allow(null, '').optional(),
    ip: Joi.string()
      .pattern(/^(\d{1,3}\.){3}\d{1,3}$/)
      .allow(null, '')
      .optional()
  })
}

const mapStatus = {
  attivo: 'Attivo',
  inattivo: 'Inattivo',
  manutenzione: 'Manutenzione'
}

const GatewayForm = ({ mode = 'create', initialData = {}, setIsValid, onChange }) => {
  const [validation, setValidation] = useState({ isValid: false })
  const [formData, setFormData] = useState({
    gatewayId: initialData.gatewayId || '',
    serial: initialData.serial || '',
    siteId: initialData.siteId || '',
    lat: initialData.lat || 0,
    lng: initialData.lng || 0,
    status: mapStatus[initialData.status] || 'Attivo',
    technology: initialData.technology || 'Lorawan',
    wmbusId: initialData.wmbusId || '',
    antennaLora: initialData.antennaLora || '',
    antennaWmbus: initialData.antennaWmbus || '',
    connectivity: initialData.connectivity || '',
    ip: initialData.ip || ''
  })

  const showLora = formData.technology === 'Lorawan' || formData.technology === 'Wmbus+Lorawan'
  const showWmbus = formData.technology === 'Wmbus' || formData.technology === 'Wmbus+Lorawan'

  const handleChange = (field) => (e) => {
    if (field === 'lat' || field === 'lng') {
      const value = e.target.value
      if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
        setFormData({ ...formData, [field]: Number.parseFloat(value) })
        return
      }
    }

    if (field === 'technology') {
      const newTech = e.target.value
      const newShowLora = newTech === 'Lorawan' || newTech === 'Wmbus+Lorawan'
      const newShowWmbus = newTech === 'Wmbus' || newTech === 'Wmbus+Lorawan'
      setFormData({
        ...formData,
        technology: newTech,
        ...(!newShowLora ? { gatewayId: null, antennaLora: null } : {}),
        ...(!newShowWmbus ? { wmbusId: null, antennaWmbus: null } : {})
      })
      return
    }

    setFormData({ ...formData, [field]: e.target.value })
  }

  useValidationEffect(() => {
    const newValidation = useFieldValidation(formData, buildSchema(formData.technology))
    setValidation(newValidation)
    setIsValid(newValidation.isValid)
    onChange(formData)
  }, [formData])

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData({
        gatewayId: initialData.gatewayId || '',
        serial: initialData.serial || '',
        siteId: initialData.siteId || '',
        lat: initialData.lat || 0,
        lng: initialData.lng || 0,
        status: mapStatus[initialData.status] || 'Attivo',
        technology: initialData.technology || 'Lorawan',
        wmbusId: initialData.wmbusId || '',
        antennaLora: initialData.antennaLora || '',
        antennaWmbus: initialData.antennaWmbus || '',
        connectivity: initialData.connectivity || '',
        ip: initialData.ip || ''
      })
    }
  }, [initialData])

  return (
    <Grid container spacing={2}>
      <Grid size={6}>
        <TextField
          id='gateway-modal-technology'
          fullWidth
          required
          select
          label={t('common.technology')}
          value={formData.technology}
          onChange={handleChange('technology')}
          error={validation.errors?.technology}
        >
          <MenuItem value="Lorawan">Lorawan</MenuItem>
          <MenuItem value="Wmbus">Wmbus</MenuItem>
          <MenuItem value="Wmbus+Lorawan">Wmbus+Lorawan</MenuItem>
        </TextField>
      </Grid>

      <Grid size={6}>
        <TextField
          id='gateway-modal-serial'
          fullWidth
          required
          disabled={mode === 'update'}
          label={t('common.serial')}
          value={formData.serial}
          onChange={handleChange('serial')}
          error={validation.errors?.serial}
          helperText={
            mode === 'update'
              ? t('gateways.cannotModify')
              : validation.errors?.serial
                ? t('common.required')
                : undefined
          }
          placeholder="12345gfd2345"
        />
      </Grid>

      {showLora && (
        <Grid size={6}>
          <TextField
            id='gateway-modal-gateway-id'
            fullWidth
            required
            label={t('gateways.gatewayId')}
            value={formData.gatewayId}
            onChange={handleChange('gatewayId')}
            error={validation.errors?.gatewayId}
            helperText={
              validation.errors?.gatewayId
                ? t('gateways.invalidHex')
                : t('gateways.hexFormat')
            }
            placeholder="0011223344556677"
          />
        </Grid>
      )}

      <Grid size={6}>
        <TextField
          id='gateway-modal-site-id'
          fullWidth
          label={t('gateways.siteId')}
          value={formData.siteId}
          onChange={handleChange('siteId')}
          error={validation.errors?.siteId}
          helperText={validation.errors?.siteId ? t('common.required') : undefined}
        />
      </Grid>

      <Grid size={6}>
        <TextField
          id='gateway-modal-status'
          fullWidth
          required
          select
          label={t('common.status')}
          value={formData.status}
          onChange={handleChange('status')}
          error={validation.errors?.status}
        >
          <MenuItem value="Attivo">{t('gateways.active')}</MenuItem>
          <MenuItem value="Inattivo">{t('common.inactive')}</MenuItem>
          <MenuItem value="Manutenzione">{t('common.maintenance')}</MenuItem>
        </TextField>
      </Grid>

      <Grid size={6}>
        <TextField
          id='gateway-modal-ip'
          fullWidth
          label={t('gateways.ip')}
          value={formData.ip}
          onChange={handleChange('ip')}
          error={validation.errors?.ip}
          helperText={validation.errors?.ip ? t('gateways.invalidIp') : undefined}
          placeholder="192.168.1.1"
        />
      </Grid>

      <Grid size={12}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              id='gateway-modal-latitude'
              fullWidth
              type="number"
              label={t('common.latitude')}
              value={formData.lat}
              onChange={handleChange('lat')}
              error={validation.errors?.lat}
              helperText={
                validation.errors?.lat
                  ? t('gateways.invalidLatitude')
                  : '-85.05112878 to 85.05112878'
              }
            />
          </Grid>

          <Grid size={6}>
            <TextField
              id='gateway-modal-longitude'
              fullWidth
              type="number"
              label={t('common.longitude')}
              value={formData.lng}
              onChange={handleChange('lng')}
              error={validation.errors?.lng}
              helperText={
                validation.errors?.lng
                  ? t('gateways.invalidLongitude')
                  : '-180 to 180'
              }
            />
          </Grid>
        </Grid>
      </Grid>

      {showWmbus && (
        <Grid size={6}>
          <TextField
            id='gateway-modal-wmbus-id'
            fullWidth
            required
            label={t('gateways.wmbusId')}
            value={formData.wmbusId}
            onChange={handleChange('wmbusId')}
            error={validation.errors?.wmbusId}
            helperText={validation.errors?.wmbusId ? t('common.required') : undefined}
          />
        </Grid>
      )}

      <Grid size={6}>
        <TextField
          id='gateway-modal-connectivity'
          fullWidth
          select
          label={t('gateways.connectivity')}
          value={formData.connectivity}
          onChange={handleChange('connectivity')}
          error={validation.errors?.connectivity}
        >
          <MenuItem value="SIM">SIM</MenuItem>
          <MenuItem value="Ethernet">Ethernet</MenuItem>
          <MenuItem value="WiFi">WiFi</MenuItem>
        </TextField>
      </Grid>

      {showLora && (
        <Grid size={6}>
          <TextField
            id='gateway-modal-antenna-lora'
            fullWidth
            required
            label={t('gateways.antennaLora')}
            value={formData.antennaLora}
            onChange={handleChange('antennaLora')}
            error={validation.errors?.antennaLora}
            helperText={validation.errors?.antennaLora ? t('common.required') : undefined}
          />
        </Grid>
      )}

      {showWmbus && (
        <Grid size={6}>
          <TextField
            id='gateway-modal-antenna-wmbus'
            fullWidth
            required
            label={t('gateways.antennaWmbus')}
            value={formData.antennaWmbus}
            onChange={handleChange('antennaWmbus')}
            error={validation.errors?.antennaWmbus}
            helperText={validation.errors?.antennaWmbus ? t('common.required') : undefined}
          />
        </Grid>
      )}

    </Grid>
  )
}

export default GatewayForm
